import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { formatDateAbbreviatedFr } from '@shared';
import { CompetitionType } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { ArrowRight, Calendar, MapPin } from 'lucide-react';

function typeGradient(type: CompetitionType | null): string {
  switch (type) {
    case CompetitionType.SINGLE:
      return 'from-blue-500 to-blue-700';
    case CompetitionType.DOUBLE:
      return 'from-emerald-500 to-teal-700';
    case CompetitionType.TRIPLE:
      return 'from-purple-500 to-purple-700';
    default:
      return 'from-slate-500 to-slate-700';
  }
}

function typeLabel(type: CompetitionType | null): string {
  switch (type) {
    case CompetitionType.SINGLE:
      return 'Tête-à-tête';
    case CompetitionType.DOUBLE:
      return 'Doublette';
    case CompetitionType.TRIPLE:
      return 'Triplette';
    default:
      return type ?? '—';
  }
}

interface DeadlineBadge {
  label: string;
  cls: string;
}

function regDeadlineBadge(registrationEnd: string | null): DeadlineBadge {
  if (!registrationEnd) return { label: '', cls: '' };
  const days = Math.ceil((new Date(registrationEnd).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return { label: 'Dernier jour !', cls: 'bg-red-100 text-red-700' };
  if (days <= 2) return { label: `Ferme dans ${days} j.`, cls: 'bg-red-100 text-red-700' };
  if (days <= 7) return { label: `Ferme dans ${days} j.`, cls: 'bg-amber-100 text-amber-700' };
  return {
    label: `Ferme le ${formatDateAbbreviatedFr(registrationEnd)}`,
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
}

export function CompetitionCard({ comp }: { comp: Competition }) {
  const deadline = regDeadlineBadge(comp.registrationEnd);
  const fill = comp.maxTeams ? Math.round((comp.currentTeams / comp.maxTeams) * 100) : null;
  const spotsLeft = comp.maxTeams !== null ? comp.maxTeams - comp.currentTeams : null;
  const concourUrl = ROUTES.CONCOUR.replace(':id', String(comp.id));
  const fillColor = fill !== null ? (fill >= 90 ? 'bg-red-400' : fill >= 65 ? 'bg-amber-400' : 'bg-emerald-400') : '';

  return (
    <Link
      to={concourUrl}
      className='group flex flex-col bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-700 overflow-hidden hover-lift transition-shadow'
    >
      {/* Colored header */}
      <div className={`bg-gradient-to-br ${typeGradient(comp.type)} p-5`}>
        <div className='flex items-start justify-between gap-2 mb-2'>
          <span className='text-white/80 text-xs font-bold uppercase tracking-wider'>{typeLabel(comp.type)}</span>
          {deadline.label && (
            <span className={`${deadline.cls} text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap`}>
              {deadline.label}
            </span>
          )}
        </div>
        <h3 className='text-white font-bold text-lg leading-snug line-clamp-2'>{comp.name}</h3>
      </div>

      {/* Body */}
      <div className='flex flex-col flex-1 p-5 gap-3'>
        {(comp.city ?? comp.venue) && (
          <div className='flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm'>
            <MapPin className='w-4 h-4 flex-shrink-0' />
            <span className='truncate'>{comp.city ?? comp.venue}</span>
            {comp.department && <span className='text-slate-400'>({comp.department})</span>}
          </div>
        )}

        <div className='flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm'>
          <Calendar className='w-4 h-4 flex-shrink-0' />
          <span>{comp.date ? formatDateAbbreviatedFr(comp.date) : '—'}</span>
        </div>

        {fill !== null && comp.maxTeams !== null && (
          <div className='mt-1'>
            <div className='flex justify-between text-xs text-slate-400 mb-1'>
              <span>
                {spotsLeft !== null && spotsLeft > 0
                  ? `${spotsLeft} place${spotsLeft > 1 ? 's' : ''} restante${spotsLeft > 1 ? 's' : ''}`
                  : 'Complet'}
              </span>
              <span>
                {comp.currentTeams}/{comp.maxTeams} équipes
              </span>
            </div>
            <div className='h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden'>
              <div
                className={`h-full rounded-full transition-all ${fillColor}`}
                style={{ width: `${Math.min(fill, 100)}%` }}
              />
            </div>
          </div>
        )}

        <div className='flex items-center justify-between mt-auto pt-2 border-t border-slate-100 dark:border-slate-800'>
          <span className='font-bold text-slate-900 dark:text-white'>
            {comp.price ? `${comp.price} €/équipe` : 'Gratuit'}
          </span>
          <span className='text-blue-600 dark:text-blue-400 font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all'>
            Voir <ArrowRight className='w-4 h-4' />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function CompetitionSkeleton() {
  return (
    <div className='flex flex-col bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-pulse'>
      <div className='h-28 bg-slate-200 dark:bg-slate-700' />
      <div className='p-5 flex flex-col gap-3'>
        <div className='h-4 bg-slate-100 dark:bg-slate-800 rounded-full w-3/4' />
        <div className='h-4 bg-slate-100 dark:bg-slate-800 rounded-full w-1/2' />
        <div className='h-2 bg-slate-100 dark:bg-slate-800 rounded-full w-full mt-2' />
        <div className='h-4 bg-slate-100 dark:bg-slate-800 rounded-full w-1/3 mt-auto' />
      </div>
    </div>
  );
}
